import { Device, L1Category, PageType } from '@gumtree/shared/src/types/client-data';
import {
    browseFooter,
    carsHomeBanner,
    carsHomeLeaderBoard,
    carsHomeMpu,
    carsHomeStickyBanner,
    fBanner,
    floatingFooter,
    galleryBanner,
    galleryMPU,
    headerBanner,
    homeBanner,
    homeSideAd,
    integratedListing,
    integratedMpu,
    integratedListing2,
    integratedMpu2,
    manageAdsLeaderboard,
    mpu,
    mpu2,
    partnershipBottom,
    partnershipWidget,
    partnershipWidget1,
    pixel,
    premiumNative1,
    rSkyB,
    rSkyB2,
    rSkyT,
    rSkyT2,
    SlotConfig,
    tBanner,
    textLink,
    textLinkBase,
    topTakeover,
    vipBanner,
    vipMiddleDesktop,
    vipMiddleMobile,
    floatingTop,
} from './slot-configs';

export type SlotConfigParams = {
    pageType: PageType;
    resultSize: number;
    device: Device | undefined;
    l1Category: L1Category | undefined;
    showNearbyAds?: boolean | { showNearbySlot1: boolean; showNearbySlot2: boolean };
    gam02Variant?: string;
};

export const resultsPages: PageType[] = ['ResultsSearch', 'ResultsBrowse'];
export const vipPages: PageType[] = ['VIP', 'eVIP', 'pVIP'];

export const buildSlotConfig = ({
    resultSize,
    l1Category,
    device,
    pageType,
    showNearbyAds,
    gam02Variant,
}: SlotConfigParams): SlotConfig[] => {
    const hasZeroResults = resultSize === 0;
    const hasLargeResults = resultSize > 14;
    const hasSmallResults = resultSize > 0 && resultSize <= 14;

    const showPremiumNative1 = Boolean(
        l1Category &&
            (
                [
                    'cars-vans-motorbikes',
                    'business-services',
                    'flats-houses',
                    'community',
                    'jobs',
                    'pets',
                ] as L1Category[]
            ).includes(l1Category)
    );


    const filterSlots = (slots: SlotConfig[]): SlotConfig[] => {
        if (!gam02Variant || gam02Variant === 'A') return slots;

        return slots.filter((slot) => {
            if (!slot) return true;

            if (gam02Variant === 'B' && slot.divId === 'pixel') {
                return false;
            }

            if (vipPages.includes(pageType) && (gam02Variant === 'B' || gam02Variant === 'C')) {
                const blacklistedUnitPos = [
                    'bottom',
                    'bottomTextLink',
                    'partnershipBottom',
                    'partnershipWidget',
                    'partnershipWidget1',
                ];

                if (blacklistedUnitPos.includes(slot.unitPos.l)) {
                    return false;
                }
            }

            return true;
        });
    };

    // UK city landing shell: top + rails + footer + pixel only (no in-feed MPU/listing)
    if (pageType === 'CityBrowseResults') {
        if (device === 'mobile') {
            return filterSlots([floatingFooter(pageType), tBanner(pageType), pixel]);
        }
        if (device === 'tablet') {
            return filterSlots([tBanner(pageType), pixel, rSkyT, rSkyB]);
        }
        if (device === 'desktop') {
            return filterSlots([pixel, tBanner(pageType), rSkyT, rSkyT2, rSkyB, rSkyB2]);
        }
        return filterSlots([pixel]);
    }

    if (resultsPages.includes(pageType)) {
        const premiumNativeSlots: SlotConfig[] = [...(showPremiumNative1 ? [premiumNative1] : [])];

        // Handle both boolean (legacy) and object (new granular control) for showNearbyAds
        const showSlot1 =
            typeof showNearbyAds === 'boolean'
                ? showNearbyAds
                : showNearbyAds?.showNearbySlot1 || false;
        const showSlot2 =
            typeof showNearbyAds === 'boolean'
                ? showNearbyAds
                : showNearbyAds?.showNearbySlot2 || false;

        const nearbyAdsSlots: SlotConfig[] = [
            ...(showSlot1 ? [integratedMpu2] : []),
            ...(showSlot2 ? [integratedListing2] : []),
        ];

        if (device === 'mobile') {
            if (hasZeroResults) {
                return filterSlots([floatingFooter(pageType), pixel]);
            } else if (hasSmallResults) {
                return filterSlots([
                    floatingFooter(pageType),
                    tBanner(pageType),
                    integratedMpu,
                    pixel,
                    ...premiumNativeSlots,
                    ...nearbyAdsSlots,
                ]);
            } else if (hasLargeResults) {
                return filterSlots([
                    floatingFooter(pageType),
                    tBanner(pageType),
                    integratedMpu,
                    integratedListing,
                    pixel,
                    ...premiumNativeSlots,
                    ...nearbyAdsSlots,
                ]);
            }
        }
        if (device === 'tablet') {
            if (hasZeroResults) {
                return filterSlots([tBanner(pageType), pixel, ...premiumNativeSlots]);
            } else if (hasSmallResults) {
                return filterSlots([
                    tBanner(pageType),
                    integratedMpu,
                    integratedListing,
                    pixel,
                    rSkyT,
                    rSkyB,
                    ...premiumNativeSlots,
                    ...nearbyAdsSlots,
                ]);
            } else if (hasLargeResults) {
                return filterSlots([
                    tBanner(pageType),
                    integratedMpu,
                    integratedListing,
                    pixel,
                    rSkyT,
                    rSkyB,
                    ...premiumNativeSlots,
                    ...nearbyAdsSlots,
                ]);
            }
        }
        if (device === 'desktop') {
            if (hasZeroResults) {
                return filterSlots([pixel, tBanner(pageType), rSkyT, ...premiumNativeSlots]);
            } else if (hasSmallResults) {
                return filterSlots([
                    integratedMpu,
                    integratedListing,
                    pixel,
                    tBanner(pageType),
                    rSkyT,
                    rSkyT2,
                    rSkyB,
                    rSkyB2,
                    ...premiumNativeSlots,
                    ...nearbyAdsSlots,
                ]);
            } else if (hasLargeResults) {
                return filterSlots([
                    integratedMpu,
                    integratedListing,
                    pixel,
                    tBanner(pageType),
                    rSkyT,
                    rSkyT2,
                    rSkyB,
                    rSkyB2,
                    ...premiumNativeSlots,
                    ...nearbyAdsSlots,
                ]);
            }
        }
    }

    if (vipPages.includes(pageType)) {
        const base = [
            partnershipWidget,
            partnershipWidget1,
            partnershipBottom,
            galleryBanner,
            galleryMPU,
            pixel,
        ];

        if (device === 'mobile') {
            // Exclude floatingFooter for Services category on mobile as the sticky header with
            // "Request a quote" button is shown at the bottom instead (bottom-sticky class)
            const shouldShowFloatingFooter = l1Category !== 'business-services';
            
            return filterSlots([
                ...base,
                ...(shouldShowFloatingFooter ? [floatingFooter(pageType)] : []),
                textLinkBase(pageType),
                vipMiddleMobile,
                floatingTop,
            ]);
        }
        if (device === 'tablet') {
            return filterSlots([
                ...base,
                vipBanner,
                textLink(pageType, showPremiumNative1),
                mpu(pageType),
                fBanner(pageType),

                vipMiddleDesktop,
            ]);
        }
        if (device === 'desktop') {
            return filterSlots([
                ...base,
                vipBanner,
                textLink(pageType, showPremiumNative1),
                mpu(pageType),
                vipMiddleDesktop,
            ]);
        }
    }

    if (pageType === 'Homepage') {
        return filterSlots([topTakeover, homeSideAd, homeBanner, pixel]);
    }

    if (pageType === 'VipGallery') {
        return [galleryBanner];
    }

    if (pageType === 'Article') {
        return [tBanner(pageType), mpu(pageType), fBanner(pageType)];
    }

    if (pageType === 'ResultsSeller' || pageType === 'UserStatic') {
        return [mpu(pageType)];
    }

    if (pageType === 'LandingPageCategory') {
        return [browseFooter];
    }

    if (['Error_404', 'Error_500'].includes(pageType)) {
        return [mpu(pageType)];
    }

    if (pageType === 'R2SEmailSuccess') {
        if (device === 'mobile') {
            return [premiumNative1];
        }
        return [headerBanner, premiumNative1];
    }

    if (pageType === 'ManageAds' || pageType === 'ManageAdsPro') {
        return [manageAdsLeaderboard];
    }

    if (pageType === 'PaymentSuccess') {
        return [manageAdsLeaderboard, mpu2];
    }

    if (pageType === 'LandingPageCars') {
        return [carsHomeBanner, carsHomeLeaderBoard, carsHomeMpu, carsHomeStickyBanner];
    }

    if (pageType === 'OrderSuccess') {
        return [premiumNative1];
    }

    return [];
};

export default buildSlotConfig;
